import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  filter,
  finalize,
  fromEvent,
  map,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import {
  type Intersection,
  type Object3D,
  type Object3DEventMap,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  useLabelingBoxStore,
  useToolMainCameraStore,
  useToolMenuStore,
} from '@/store';
import type { ThreeState } from '@/type';
import {
  type NEW__LabelingBox,
  filterOrRestart,
  withCanvasXY,
  withTarget,
} from '@/util';

import { isTopMode, setControlEnabled } from '../util';

const EDGE_RAY_LINE_THRESHOLD = 0.3;
const LINE_HOVER_COLOR = 'yellow';
const VECTOR3_FOR_PLANE = new Vector3();

export const useDefaultViewTopResize = ({
  setRenderBoxListById,
  disabled,
}: {
  setRenderBoxListById: (boxIdList: string[]) => void;
  disabled: boolean;
}) => {
  const getThree = useThree(state => state.get as () => ThreeState);
  const isMoving = useEditingLabelBoxStore(state => state.isMoving);
  const isResizing = useEditingLabelBoxStore(state => state.isResizing);
  const isRotating = useEditingLabelBoxStore(state => state.isRotating);
  const { labelingBoxList } = useLabelingBoxContext();
  const labelingBoxListRef = useRef<NEW__LabelingBox[]>([]);
  const isMovingRef = useRef(false);
  const isResizingRef = useRef(false);
  const isRotatingRef = useRef(false);

  const getSelectedBox = () => {
    const selectedBoxId = Object.keys(
      useLabelingBoxStore.getState().selectedBoxIdMap,
    )[0];

    if (!selectedBoxId) return null;

    const selectedBox = labelingBoxListRef.current.find(
      box => box.id === selectedBoxId,
    );

    return selectedBox ?? null;
  };

  const getLabelingBoxById = (id: string) =>
    labelingBoxListRef.current.find(box => box.id === id);

  const cleanupResize = () => {
    useEditingLabelBoxStore.getState().clear();
    setControlEnabled(true, getThree().controls);
    setRenderBoxListById([]);

    const selectedBoxId = getSelectedBox()?.id;

    if (selectedBoxId) {
      useLabelingBoxStore.getState().selectBox(selectedBoxId);
    }
  };

  useSubscriptions(() => {
    if (disabled) {
      cleanupResize();

      return [];
    }

    const isAiAssistRunning = useToolMenuStore
      .getState()
      .isSelectedByToolName('aiAssist');

    return [
      fromEvent<MouseEvent>(window, 'mousemove')
        .pipe(
          filter(() => isTopMode(useToolMainCameraStore.getState().getMode())),
          filter(() => !getThree().camera.isPerspectiveCamera),
          filter(() => !isAiAssistRunning),
          filter(() => !!getSelectedBox()),
          withTarget,
          withCanvasXY,
          map(e => {
            const selectedBox = getSelectedBox()!;
            const raycaster = new Raycaster();
            raycaster.setFromCamera(
              new Vector2(e.canvasX, e.canvasY),
              getThree().camera,
            );

            const edgeRay = new Raycaster();

            edgeRay.params.Line.threshold = EDGE_RAY_LINE_THRESHOLD;
            edgeRay.setFromCamera(
              new Vector2(e.canvasX, e.canvasY),
              getThree().camera,
            );

            const lineIntersect = edgeRay.intersectObject(selectedBox.line2);

            !isResizingRef.current && lineIntersect.at(0)
              ? selectedBox.setLineColorByIndex(
                  lineIntersect.at(0)!.faceIndex!,
                  LINE_HOVER_COLOR,
                )
              : selectedBox.color
                ? selectedBox.updateColor(selectedBox.color)
                : void 0;

            const grabIntersectionsList = [lineIntersect];
            const canvas = e.target?.closest('canvas') as HTMLCanvasElement;

            if (canvas) {
              if (isResizing || isMoving || isRotating) {
                canvas.style.setProperty('cursor', 'grabbing', 'important');
                canvas.setAttribute('data-cursor-priority', 'editing');
              } else if (
                grabIntersectionsList.some(
                  intersections => !!intersections.length,
                )
              ) {
                canvas.style.setProperty('cursor', 'grab', 'important');
                canvas.setAttribute('data-cursor-priority', 'editing');
              } else {
                canvas.removeAttribute('data-cursor-priority');
              }
            }
          }),
        )
        .subscribe(),

      fromEvent<PointerEvent>(window, 'pointerdown')
        .pipe(
          filter(e => e.buttons === 1),
          filter(() => !!getSelectedBox()),
          filter(() => !isMovingRef.current),
          filter(() => !isRotatingRef.current),
          withTarget,
          withCanvasXY,
          map(e => {
            const selectedBox = getSelectedBox();

            if (!selectedBox) return null;

            const raycaster = new Raycaster();

            raycaster.params.Line.threshold = EDGE_RAY_LINE_THRESHOLD;
            raycaster.setFromCamera(
              new Vector2(e.canvasX, e.canvasY),
              getThree().camera,
            );

            const intersections = raycaster.intersectObject(
              selectedBox.line2,
              false,
            );

            return {
              canvasX: e.canvasX,
              canvasY: e.canvasY,
              intersections,
              selectedBox,
            };
          }),
          filter(value => !!value),
          filter(({ intersections }) => intersections.length > 0),
          switchMap(
            ({
              canvasX,
              canvasY,
              intersections,
              selectedBox,
            }: {
              canvasX: number;
              canvasY: number;
              intersections: Intersection<Object3D<Object3DEventMap>>[];
              selectedBox: NEW__LabelingBox;
            }) => {
              const [targetLine] = intersections;
              const startPoint = targetLine.point.clone();
              const planeNormal = getThree()
                .camera.getWorldDirection(VECTOR3_FOR_PLANE)
                .clone()
                .negate();
              const plane = new Plane();
              const mouse = new Vector2(canvasX, canvasY);
              const raycaster = new Raycaster();

              plane.setFromNormalAndCoplanarPoint(planeNormal, startPoint);
              raycaster.setFromCamera(mouse, getThree().camera);

              const startMousePosition = new Vector3();
              const startPointWorld = targetLine.point.clone();
              const initSize = selectedBox.boxMesh.scale.clone();
              const initPosition = selectedBox.boxMesh.position.clone();
              const quaternion = selectedBox.boxMesh.quaternion;
              const inverseQuaternion = quaternion.clone().invert();

              raycaster.ray.intersectPlane(plane, startMousePosition);

              selectedBox.setLineColorByIndex(
                targetLine.faceIndex!,
                selectedBox.color!,
              );

              useEditingLabelBoxStore.getState().setIsResizing(true);
              useEditingLabelBoxStore.getState().setIsTransforming(true);

              if (getThree().controls) {
                getThree().controls.enablePan = false;
                getThree().controls.update();
              }

              return fromEvent<PointerEvent>(window, 'pointermove').pipe(
                withTarget,
                filterOrRestart(e => e.target instanceof HTMLCanvasElement),
                withCanvasXY,
                tap(event => {
                  const targetLabelingBox = getLabelingBoxById(selectedBox.id);

                  if (!targetLabelingBox) return;

                  const mouse = new Vector2(event.canvasX, event.canvasY);
                  const raycaster = new Raycaster();
                  raycaster.setFromCamera(mouse, getThree().camera);

                  const currentPoint = new Vector3();
                  raycaster.ray.intersectPlane(plane, currentPoint);

                  const worldDelta = currentPoint.clone().sub(startPointWorld);

                  const edgeIndex = targetLine.faceIndex!;
                  const isXEdge = edgeIndex === 12 || edgeIndex === 16;
                  const isYEdge = edgeIndex === 14 || edgeIndex === 10;
                  const box = targetLabelingBox.boxMesh;
                  const minSize = 0.1;

                  const newSize = box.scale.clone();
                  const newPosition = box.position.clone();
                  const localDelta = worldDelta
                    .clone()
                    .applyQuaternion(inverseQuaternion);

                  const localPositionOffset = new Vector3();

                  if (isXEdge) {
                    const direction = edgeIndex === 16 ? -1 : 1;
                    const delta = localDelta.x * direction;
                    const newWidth = Math.max(minSize, initSize.x + delta);

                    newSize.x = newWidth;
                    localPositionOffset.x = localDelta.x / 2;
                  }

                  if (isYEdge) {
                    const direction = edgeIndex === 10 ? -1 : 1;
                    const delta = localDelta.y * direction;
                    const newHeight = Math.max(minSize, initSize.y + delta);

                    newSize.y = newHeight;
                    localPositionOffset.y = localDelta.y / 2;
                  }

                  const worldPositionOffset = localPositionOffset
                    .clone()
                    .applyQuaternion(quaternion);

                  newPosition.copy(initPosition).add(worldPositionOffset);

                  targetLabelingBox.updatePosition(newPosition.toArray());
                  targetLabelingBox.updateDimension(newSize.toArray());
                }),
                takeUntil(
                  fromEvent<PointerEvent>(window, 'pointerup').pipe(
                    tap(() => {
                      const targetLabelingBox = getLabelingBoxById(
                        selectedBox.id,
                      );

                      if (!targetLabelingBox) return;

                      useLabelingBoxStore.temporal.getState().pause();
                      useLabelingBoxStore
                        .getState()
                        .moveBoxList([
                          [selectedBox.id, targetLabelingBox.position],
                        ]);
                      useLabelingBoxStore
                        .getState()
                        .resizeBoxList([
                          [selectedBox.id, targetLabelingBox.dimension],
                        ]);
                      useLabelingBoxStore.temporal.getState().resume();
                    }),
                    switchMap(() => timer(8)),
                    finalize(() => cleanupResize()),
                  ),
                ),
              );
            },
          ),
          switchMap(() => timer(8)),
          finalize(() => cleanupResize()),
        )
        .subscribe(),
    ];
  }, [disabled]);

  useEffect(() => {
    labelingBoxListRef.current = labelingBoxList;
  }, [labelingBoxList]);

  useEffect(() => {
    isMovingRef.current = isMoving;
    isResizingRef.current = isResizing;
    isRotatingRef.current = isRotating;
  }, [isMoving, isResizing, isRotating]);
};
